import React from 'react'
import Image from 'next/image'
import moment from 'moment'
import styled from '@emotion/styled'
import { mq } from '../../helpers'
import { PublicationLogo } from './styles'

export default function PressArticle({ article }) {
  if (!article) {
    return null
  }
  const {
    title, subtitle, date, summary, original_url,
    publication_name, publication_logo, thumbnail, thumbnail_caption, body,
  } = article

  // The Strapi service resolves media URLs before they enter page props.
  const logo = publication_logo?.url || ''
  const thumb = thumbnail?.url || ''

  return (
    <ArticleWrapper>
      <Hero>
        <div className="hero-inner">
          {date && <p className="date">{moment(date).format('MMMM D, YYYY')}</p>}
          <h1>{title}</h1>
          {subtitle && <p className="subtitle">{subtitle}</p>}
        </div>
      </Hero>

      <Body>
        <div className="as-seen-in">
          <span>As seen in:</span>
          {logo && (
            <PublicationLogo>
              <Image src={logo} alt={publication_name || ''} fill />
            </PublicationLogo>
          )}
          {original_url && (
            <a className="original" href={original_url} target="_blank" rel="noopener noreferrer">
              Read the original article
            </a>
          )}
        </div>

        {summary && <p className="summary">{summary}</p>}

        {thumb && (
          <figure className="feature-image">
            <div className="img">
              <Image src={thumb} alt={title || ''} fill style={{ objectFit: 'cover' }} />
            </div>
            {thumbnail_caption && <figcaption>{thumbnail_caption}</figcaption>}
          </figure>
        )}

        <div
          className="injected"
          dangerouslySetInnerHTML={{ __html: body || '' }}
        />
      </Body>
    </ArticleWrapper>
  )
}

const ArticleWrapper = styled.div`
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: clip;
  padding-bottom: 60px;
`

const Hero = styled.div`
  width: 100%;
  background-color: #f9f6f1;
  padding: 60px 0;
  ${mq('max', '768px')} {
    padding: 30px 0;
  }
  .hero-inner {
    max-width: 750px;
    margin: 0 auto;
    padding: 0 16px;
    box-sizing: border-box;
    text-align: left;
  }
  .date {
    color: var(--Dark-grey, #545454);
    font-family: Euclid;
    font-size: 16px;
    margin: 0 0 12px;
  }
  h1 {
    color: var(--Black, #000);
    font-family: Garnett;
    font-size: 56px;
    font-weight: 750;
    line-height: 120%;
    margin: 0;
    ${mq('max', '768px')} {
      font-size: 30px;
    }
  }
  .subtitle {
    color: var(--Black, #000);
    font-family: Euclid;
    font-size: 14px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin: 16px 0 0;
  }
`

const Body = styled.div`
  width: 100%;
  max-width: 750px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 40px 16px 0;

  .as-seen-in {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 32px;
    span { font-family: Euclid; color: var(--Dark-grey, #545454); font-size: 16px; }
    .original {
      color: var(--Blue, #07218F);
      font-family: Euclid;
      font-weight: 600;
      text-decoration: underline;
      width: fit-content;
    }
  }

  .summary {
    font-family: Euclid;
    font-size: 20px;
    line-height: 138%;
    color: #000;
    margin: 0 0 32px;
  }

  .feature-image {
    margin: 0 0 32px;
    .img { position: relative; aspect-ratio: 16 / 9; width: 100%; }
    figcaption {
      font-family: Euclid;
      font-size: 13px;
      color: var(--Dark-grey, #545454);
      margin-top: 8px;
    }
    ${mq('max', '768px')} {
      margin-left: -16px;
      margin-right: -16px;
      figcaption {
        padding: 0 16px;
      }
    }
  }

  .injected {
    overflow-wrap: anywhere;
    p, span, li {
      line-height: 138%;
      color: #000;
      font-family: Euclid;
      font-size: 20px;
    }
    a {
      color: var(--Black, #000);
      text-decoration: underline;
    }
    img { width: 100%; height: auto; }
  }
`
